import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  ICustomMappingHandler,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  DEFAULT_SCHEDULE_ID,
  FeeSchedule,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IFeeSchedule,
  IOrganisation,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  INamedDocument,
  PrimaryKey,
  WithPrimaryKey,
  WithRef,
  asDocRef,
  doc,
  getError,
  snapshotCombineLatest,
  toNamedDocument,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, combineLatest, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FEE_SCHEDULE_RESOURCE_TYPE } from '../../mappings/fee-schedules';
import { PracticeMigration } from '../../practice-migrations';
import { PraktikaFeeSchedule } from '../../praktika/source/entities/fee-schedule';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';

export interface IFeeScheduleDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  feeScheduleRef: DocumentReference<IFeeSchedule>;
}

export interface IFeeScheduleJobData<
  FeeScheduleRecord extends ISourceEntityTypes,
> {
  feeScheduleRecord: IGetRecordResponse<FeeScheduleRecord>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  brand: WithRef<IBrand>;
  feeScheduleMappings: WithPrimaryKey<ITranslationMap<IFeeSchedule>>[];
}

export interface IFeeScheduleMigrationData {
  isDefault: boolean;
  sourceFeeScheduleId: string;
  feeSchedule: IFeeSchedule;
  practiceRef?: DocumentReference<IPractice>;
  organisationRef: DocumentReference<IOrganisation>;
}

export const FEE_SCHEDULE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: FEE_SCHEDULE_RESOURCE_TYPE,
    label: 'Fee Schedules',
    description:
      'Fee Schedule custom mapping must be set after this migration is run.',
  },
});

export abstract class BaseFeeScheduleDestination<
  FeeScheduleRecord extends ISourceEntityTypes,
> extends BaseDestinationEntity<
  IFeeScheduleDestinationRecord,
  IFeeScheduleJobData<FeeScheduleRecord>,
  IFeeScheduleMigrationData
> {
  abstract feeScheduleSourceEntity: ISourceEntityHandler<FeeScheduleRecord>;
  abstract practiceCustomMapping: ICustomMappingHandler<IPractice>;
  abstract feeScheduleCustomMapping: ICustomMappingHandler<IFeeSchedule>;

  destinationEntity = FEE_SCHEDULE_DESTINATION_ENTITY;

  get sourceCountComparison(): ISourceEntityHandler<FeeScheduleRecord> {
    return this.feeScheduleSourceEntity;
  }

  getDestinationEntityRecordUid(
    data: IFeeScheduleJobData<FeeScheduleRecord>
  ): string {
    return data.feeScheduleRecord.record.uid;
  }

  sourceCountDataAccessor(
    data: IFeeScheduleJobData<FeeScheduleRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.feeScheduleRecord.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IFeeScheduleDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return from(Firestore.getDoc(record.data.feeScheduleRef)).pipe(
      map((feeSchedule) => [
        {
          label: 'Fee Schedule',
          data: feeSchedule,
        },
      ])
    );
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IFeeScheduleJobData<FeeScheduleRecord>[]> {
    const practices$ = this.practiceCustomMapping.getRecords(
      translationMapHandler
    );
    const feeScheduleMappings$ = this.feeScheduleCustomMapping.getRecords(
      translationMapHandler
    );
    const brand$ = PracticeMigration.brand$(migration);

    return snapshotCombineLatest([
      brand$,
      practices$,
      feeScheduleMappings$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.feeScheduleSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([feeScheduleRecords, [brand, practices, feeScheduleMappings]]) =>
        feeScheduleRecords.map((feeScheduleRecord) => ({
          feeScheduleRecord,
          brand,
          practices,
          feeScheduleMappings,
        }))
      )
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IFeeScheduleMigrationData
  ): Promise<IFeeScheduleMigrationData | undefined> {
    let existingFeeScheduleRef = await translationMap.getDestination(
      data.sourceFeeScheduleId,
      FEE_SCHEDULE_RESOURCE_TYPE
    );

    if (!existingFeeScheduleRef && data.isDefault) {
      existingFeeScheduleRef = doc(
        data.practiceRef
          ? FeeSchedule.col({
              ref: data.practiceRef,
            })
          : FeeSchedule.col({
              ref: data.organisationRef,
            }),
        DEFAULT_SCHEDULE_ID
      );
    }

    if (!existingFeeScheduleRef) {
      return;
    }

    const existingFeeSchedule = await Firestore.getDoc(
      existingFeeScheduleRef as DocumentReference<IFeeSchedule>
    );

    const hasMergeConflict = hasMergeConflicts(
      data.feeSchedule,
      existingFeeSchedule
    );

    if (hasMergeConflict) {
      return {
        ...data,
        feeSchedule: existingFeeSchedule,
      };
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IFeeScheduleJobData<FeeScheduleRecord>,
    _migrationData: IFeeScheduleMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.feeScheduleRecord.record.uid,
      label: jobData.feeScheduleRecord.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.feeScheduleRecord.record.id,
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IFeeScheduleJobData<FeeScheduleRecord>,
    migrationData: IFeeScheduleMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const mapping = jobData.feeScheduleMappings.find(
        (feeScheduleMapping) =>
          feeScheduleMapping.sourceIdentifier ===
          migrationData.sourceFeeScheduleId
      );

      if (mapping?.destinationIdentifier) {
        await translationMap.upsert({
          sourceIdentifier: migrationData.sourceFeeScheduleId,
          destinationIdentifier: mapping?.destinationIdentifier,
          resourceType: FEE_SCHEDULE_RESOURCE_TYPE,
        });
        return this.buildSkippedResponse(jobData);
      }

      const existingFeeScheduleRef = migrationData.isDefault
        ? doc(
            FeeSchedule.col({
              ref: migration.configuration.organisation.ref,
            }),
            DEFAULT_SCHEDULE_ID
          )
        : await translationMap.getDestination(
            migrationData.sourceFeeScheduleId,
            FEE_SCHEDULE_RESOURCE_TYPE
          );

      const feeScheduleRef = await FirestoreMigrate.upsertDoc(
        FeeSchedule.col({
          ref: migration.configuration.organisation.ref,
        }),
        migrationData.feeSchedule,
        existingFeeScheduleRef?.id
      );

      if (!existingFeeScheduleRef) {
        await translationMap.upsert({
          sourceIdentifier: migrationData.sourceFeeScheduleId,
          destinationIdentifier: feeScheduleRef,
          resourceType: FEE_SCHEDULE_RESOURCE_TYPE,
        });
      }

      return this._buildSuccessResponse(
        jobData.feeScheduleRecord,
        feeScheduleRef
      );
    } catch (error) {
      return this._buildErrorResponse(
        jobData.feeScheduleRecord.record,
        getError(error)
      );
    }
  }

  buildSkippedResponse(
    data: IFeeScheduleJobData<FeeScheduleRecord>
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.feeScheduleRecord.record.uid,
      label: data.feeScheduleRecord.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.feeScheduleRecord.record.id,
    };
  }

  protected _buildErrorResponse(
    feeSchedule: Pick<
      IGetRecordResponse<PraktikaFeeSchedule>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: feeSchedule.uid,
      label: feeSchedule.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: feeSchedule.id,
      errorMessage: errorMessage ?? `Can't resolve fee schedule data`,
      failData: {
        feeScheduleRef: feeSchedule.id,
      },
    };
  }

  protected _buildSuccessResponse(
    record: IGetRecordResponse<FeeScheduleRecord>,
    feeScheduleRef: DocumentReference<IFeeSchedule>
  ): IDestinationEntityRecord<IFeeScheduleDestinationRecord> {
    return {
      uid: record.record.uid,
      label: record.record.label,
      data: {
        sourceRef: record.record.id,
        feeScheduleRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: record.record.id,
      migratedAt: toTimestamp(),
    };
  }
}

export type FeeScheduleResolverFn = (
  id?: string
) => Promise<INamedDocument<IFeeSchedule>>;

export function feeScheduleResolverFn(
  translationMap: TranslationMapHandler,
  defaultFeeSchedule: INamedDocument<IFeeSchedule>
): FeeScheduleResolverFn {
  return async (id?: string) => {
    if (!id) {
      return toNamedDocument(defaultFeeSchedule);
    }
    const resolved = await translationMap.getBySource(
      id,
      FEE_SCHEDULE_RESOURCE_TYPE
    );
    if (!resolved || !resolved.destinationIdentifier) {
      return toNamedDocument(defaultFeeSchedule);
    }
    return toNamedDocument(
      await Firestore.getDoc(
        asDocRef<IFeeSchedule>(resolved.destinationIdentifier)
      )
    );
  };
}
